import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { expensesApi } from '@/api/expenses';
import { downloadBlob } from '@/lib/downloadBlob';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

type TdsTab = 'all' | 'unpaid' | 'paid';

function tdsPaidParam(tab: TdsTab): string | undefined {
  if (tab === 'paid') return 'true';
  if (tab === 'unpaid') return 'false';
  return undefined;
}

export default function CaTdsReports() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TdsTab>('all');
  const [exporting, setExporting] = useState(false);
  const [tdsDialogOpen, setTdsDialogOpen] = useState(false);
  const [tdsChallanDraft, setTdsChallanDraft] = useState('');
  const [tdsTarget, setTdsTarget] = useState<{ kind: 'patient' | 'central'; id: string } | null>(
    null
  );
  const {
    fromIso,
    toIso,
    selectedInterval,
    setSelectedInterval,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    handleTimeIntervalChange,
  } = useReportDateRange('this_month');

  const tdsPaid = tdsPaidParam(tab);

  const { data: rows, isLoading } = useQuery({
    queryKey: ['ca-tds-report', fromIso, toIso, tdsPaid],
    queryFn: async () => {
      const res = await expensesApi.listTdsStatus({
        from: fromIso,
        to: toIso,
        ...(tdsPaid !== undefined ? { tdsPaid } : {}),
      });
      return res.data?.data ?? [];
    },
    enabled: !!fromIso && !!toIso,
  });

  const dateSuffix =
    fromIso && toIso
      ? `${format(new Date(fromIso), 'yyyy-MM-dd')}-to-${format(new Date(toIso), 'yyyy-MM-dd')}`
      : 'report';

  const runExport = async () => {
    if (!fromIso || !toIso) return;
    setExporting(true);
    try {
      const resp = await expensesApi.exportTdsStatus({
        from: fromIso,
        to: toIso,
        ...(tdsPaid !== undefined ? { tdsPaid } : {}),
      });
      const suffix = tab === 'all' ? 'all' : tab;
      downloadBlob(
        new Blob([resp.data], { type: 'text/csv;charset=utf-8' }),
        `TDS-Status-${suffix}-${dateSuffix}.csv`
      );
      toast.success('Download started');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const totalAmount = (rows ?? []).reduce((s: number, r: any) => s + (r.amount ?? 0), 0);

  const markTdsPaidMut = useMutation({
    mutationFn: async (payload: {
      kind: 'patient' | 'central';
      id: string;
      challanNo?: string | null;
    }) => {
      if (payload.kind === 'patient') {
        return expensesApi.markPatientTdsPaid(payload.id, { tdsChallanNo: payload.challanNo ?? null });
      }
      return expensesApi.markCentralTdsPaid(payload.id, { tdsChallanNo: payload.challanNo ?? null });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ca-tds-report'] });
      toast.success('TDS marked paid');
      setTdsDialogOpen(false);
      setTdsTarget(null);
      setTdsChallanDraft('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to mark TDS paid'),
  });

  const openMarkTdsPaid = (kind: 'patient' | 'central', id: string) => {
    setTdsTarget({ kind, id });
    setTdsChallanDraft('');
    setTdsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">TDS reports</h2>
        <p className="text-slate-400">
          Expenses where TDS was deducted. View paid vs unpaid, mark deposited TDS as paid, and download for filing.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-4">
        <TimeIntervalFilter
          onTimeIntervalChange={handleTimeIntervalChange}
          selectedInterval={selectedInterval}
          onIntervalChange={setSelectedInterval}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
        <Button
          variant="outline"
          className="border-white/15 bg-white/5 text-white hover:bg-white/10"
          disabled={!fromIso || !toIso || exporting}
          onClick={runExport}
        >
          <Download className="mr-2 h-4 w-4" />
          {exporting ? 'Exporting…' : 'Download CSV'}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TdsTab)}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="all">All TDS</TabsTrigger>
          <TabsTrigger value="unpaid">Unpaid</TabsTrigger>
          <TabsTrigger value="paid">Paid</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs text-slate-400">Rows</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-white">
                {(rows ?? []).length.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs text-slate-400">Total amount</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-white">
                ₹{totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
            {isLoading ? (
              <p className="p-4 text-slate-400">Loading…</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-400">Expense ID</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Service month</TableHead>
                      <TableHead className="text-slate-400">Type</TableHead>
                      <TableHead className="text-slate-400">Category</TableHead>
                      <TableHead className="text-slate-400">Vendor</TableHead>
                      <TableHead className="text-slate-400">PAN</TableHead>
                      <TableHead className="text-slate-400">Amount</TableHead>
                      <TableHead className="text-slate-400">Invoice</TableHead>
                      <TableHead className="text-slate-400">TDS paid</TableHead>
                      <TableHead className="text-slate-400">Challan</TableHead>
                      <TableHead className="text-slate-400">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(rows ?? []).map((r: any) => {
                      const proofs =
                        Array.isArray(r.proofs) && r.proofs.length > 0
                          ? r.proofs
                          : [];
                      return (
                      <TableRow key={`${r.kind}-${r.id}`} className="border-white/10">
                        <TableCell className="whitespace-nowrap font-mono text-xs text-teal-300">
                          {r.expenseNumber || '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-white">
                          {format(new Date(r.expenseDate), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-slate-300">
                          {r.serviceMonth || '—'}
                        </TableCell>
                        <TableCell className="capitalize text-slate-300">{r.kind}</TableCell>
                        <TableCell className="text-slate-300">{r.category}</TableCell>
                        <TableCell className="max-w-[160px] truncate text-slate-300">
                          {r.vendorName || '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap uppercase text-slate-300">
                          {r.panNumber || '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-200">
                          ₹{Number(r.amount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {proofs.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {proofs.map((p: { id: string; url: string; fileName: string }) => (
                                <a
                                  key={p.id}
                                  href={p.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-teal-300 underline-offset-4 hover:underline"
                                >
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                  <span className="truncate max-w-[100px]" title={p.fileName}>
                                    {p.fileName || 'View'}
                                  </span>
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.tdsPaid ? (
                            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-400">
                              Paid
                            </span>
                          ) : (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-400">
                              Unpaid
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-400">{r.tdsChallanNo || '—'}</TableCell>
                        <TableCell>
                          {!r.tdsPaid ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-white/15 bg-white/5 text-white hover:bg-white/10"
                              onClick={() => openMarkTdsPaid(r.kind, r.id)}
                            >
                              Mark paid
                            </Button>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {(!rows || rows.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center text-slate-500 py-8">
                          No TDS records for this period.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog
        open={tdsDialogOpen}
        onOpenChange={(o) => {
          setTdsDialogOpen(o);
          if (!o) setTdsTarget(null);
        }}
      >
        <DialogContent className="border-white/10 bg-slate-900 text-white">
          <DialogHeader>
            <DialogTitle>Mark TDS paid</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label className="text-slate-300">Challan no (optional)</Label>
              <Input
                value={tdsChallanDraft}
                onChange={(e) => setTdsChallanDraft(e.target.value)}
                placeholder="e.g. Challan reference"
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-white/15 bg-white/5 text-white hover:bg-white/10"
              onClick={() => setTdsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={markTdsPaidMut.isPending || !tdsTarget}
              onClick={() => {
                if (!tdsTarget) return;
                markTdsPaidMut.mutate({
                  kind: tdsTarget.kind,
                  id: tdsTarget.id,
                  challanNo: tdsChallanDraft.trim() || null,
                });
              }}
            >
              {markTdsPaidMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
